import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import {
  LayoutDashboard, Users, CalendarDays, BarChart3,
  Settings, LogOut, Search, Bell, Building2,
  // MessageCircle lo usa «WhatsApp masivo» en CORE_NAV_ITEMS. Faltaba: el
  // arreglo se construye al cargar el módulo, así que un icono sin importar
  // revienta la barra lateral ENTERA antes de pintar nada.
  Menu, X, CreditCard, Link2, Radio, Brain, Mail, MessageSquare, MessageCircle, Zap, Settings2,
  ShieldCheck, Share2, Globe,
  // El interruptor del modo oscuro.
  Moon, Sun, Megaphone, QrCode,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useMarca, urlDeLogo } from '@/lib/marca';
import { useModoOscuro } from '@/lib/modoOscuro';
import { useLocale, LanguageSelect } from '@/contexts/LocaleContext';
import { usePermisos, moduleForPath } from '@/lib/permisos';

const ADVANCED_MODE_KEY = 'tenant_nav_advanced_mode';

// Roles con acceso restringido: si un item no declara `roles`, todos los
// demás roles (owner/admin/manager/user) lo ven — solo tenant_cajero
// necesita una lista blanca explícita (únicamente Administración).
const CAJERO_ROLE = 'tenant_cajero';

const CORE_NAV_ITEMS = [
  { to: '/tenant', icon: LayoutDashboard, labelKey: 'nav.dashboard', end: true, module: 'dashboard' },
  { to: '/tenant/inbox', icon: MessageSquare, labelKey: 'nav.inbox', module: 'inbox' },
  { to: '/tenant/crm', icon: Users, labelKey: 'nav.crm', module: 'crm' },
  { to: '/tenant/agenda', icon: CalendarDays, labelKey: 'nav.agenda', module: 'agenda' },
  { to: '/tenant/business', icon: Building2, labelKey: 'nav.business', module: 'settings' },
  { to: '/tenant/administracion', icon: ShieldCheck, labelKey: 'nav.admin', module: 'pos' },
  { to: '/tenant/email', icon: Mail, labelKey: 'nav.email', module: 'email' },
  { to: '/tenant/whatsapp', icon: MessageCircle, labelKey: 'nav.whatsapp', module: 'marketing' },
  { to: '/tenant/marketing', icon: Megaphone, labelKey: 'nav.marketing', module: 'marketing' },
];

const ADVANCED_NAV_ITEMS = [
  { to: '/tenant/plan', icon: CreditCard, labelKey: 'nav.plan', module: 'billing' },
  { to: '/tenant/integrations', icon: Link2, labelKey: 'nav.integrations', module: 'integrations' },
  { to: '/tenant/setup', icon: Zap, labelKey: 'nav.setup', module: 'flows' },
  { to: '/tenant/ai', icon: Brain, labelKey: 'nav.ai', module: 'ai' },
  { to: '/tenant/reports', icon: BarChart3, labelKey: 'nav.reports', module: 'reports' },
  { to: '/tenant/social-ads', icon: Share2, labelKey: 'nav.social', modules: ['social', 'meta_ads'] },
  { to: '/tenant/web', icon: Globe, labelKey: 'nav.web', module: 'web' },
];

export default function TenantLayout() {
  const { user, logout, isImpersonating } = useAuth();
  const { t } = useLocale();
  const { puedeVer, puedeVerRuta, cargado: permisosCargados } = usePermisos();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(() => {
    try { return localStorage.getItem(ADVANCED_MODE_KEY) === 'true'; } catch { return false; }
  });
  const navigate = useNavigate();
  const location = useLocation();
  const isCajero = user?.tenant_role === CAJERO_ROLE;
  // La marca del negocio: su logo sustituye al nombre de CavernicolApp arriba.
  // tenant_id en el hook: sin él la caché de marca podía pintar otro negocio.
  const marca = useMarca(user?.tenant_id);
  const [oscuro, alternarOscuro] = useModoOscuro();

  // El color del negocio. Las dos variables se ponen en el contenedor del panel
  // y NO en `:root`, porque las variables CSS se heredan hacia abajo: dentro del
  // panel manda el color del negocio y fuera —la página pública de
  // CavernicolApp, las pantallas legales— sigue el rosa de casa. En `:root`
  // habríamos repintado el sitio público en cuanto un cliente eligiera verde
  // militar. El atributo `data-marca` es lo que enciende la capa de index.css
  // que traduce el rosa escrito a mano al color elegido.
  // En modo oscuro manda la paleta oscura del negocio, no la clara: un acento
  // oscuro sobre fondo oscuro desaparece, que es la razón de que sean dos
  // listas. Como todo el sistema lee `--cv-pink`, cambiar aquí el valor repinta
  // los detalles sin que ninguna pantalla tenga que saber en qué modo está —
  // el monito de carga incluido.
  //
  // `--cv-on-accent` = texto ENCIMA de botones rellenos (blanco o casi-negro
  // según luminancia del acento). Sin esto, un amarillo/cielo con label del
  // color «oscuro de detalles» se pierde a la vista.
  // `--cv-accent-ink` = cuando el acento se usa como COLOR DE TEXTO sobre la
  // superficie (enlaces, outline): en oscuro debe ser claro (hielo/pastel).
  const acento = oscuro
    ? {
        hex: marca?.acento_oscuro_hex || '#FF5CA8',
        hover: marca?.acento_oscuro_hover || '#FF85BF',
        on: marca?.acento_oscuro_on || '#0B1220',
        ink: marca?.acento_oscuro_ink || '#F8FAFC',
      }
    : {
        hex: marca?.acento_hex || '#F72585',
        hover: marca?.acento_hover || '#D81B60',
        on: marca?.acento_on || '#FFFFFF',
        ink: marca?.acento_ink || '#F72585',
      };
  const colorDelNegocio = {
    '--cv-pink': acento.hex,
    '--cv-pink-hover': acento.hover || acento.hex,
    '--cv-on-accent': acento.on,
    '--cv-accent-ink': acento.ink,
  };

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // El icono de la pestaña del navegador. Es el otro sitio donde el espacio es
  // cuadrado, y el más visible: el dueño tiene su sistema abierto todo el día y
  // lo reconoce por ahí. Se restaura el de CavernicolApp al salir del panel,
  // porque el icono es del documento entero y la pantalla de acceso o la página
  // pública no son del negocio.
  useEffect(() => {
    const enlace = document.querySelector("link[rel='icon']");
    if (!enlace || !marca?.tiene_logo_cuadrado) return;
    // Nunca aplicar logo de un tenant que no es el de la sesión.
    if (user?.tenant_id && marca.tenant_id && String(marca.tenant_id) !== String(user.tenant_id)) return;
    const original = enlace.getAttribute('href');
    enlace.setAttribute('href', urlDeLogo(marca.tenant_id, 'square', marca.sello_cuadrado));
    return () => { if (original) enlace.setAttribute('href', original); };
  }, [marca, user?.tenant_id]);

  // El nombre del negocio en el título de la pestaña, por el mismo motivo.
  useEffect(() => {
    if (!marca?.nombre_negocio) return;
    if (user?.tenant_id && marca.tenant_id && String(marca.tenant_id) !== String(user.tenant_id)) return;
    const original = document.title;
    document.title = marca.nombre_negocio;
    return () => { document.title = original; };
  }, [marca, user?.tenant_id]);

  useEffect(() => {
    try { localStorage.setItem(ADVANCED_MODE_KEY, String(showAdvanced)); } catch {}
  }, [showAdvanced]);

  // El cajero solo tiene acceso a Administración — si intenta entrar a
  // cualquier otra ruta de tenant (directo por URL o un enlace viejo),
  // lo mandamos de regreso. Esto complementa el filtrado de menú de abajo,
  // que solo oculta el enlace pero no bloquea la navegación directa.
  // S196: bloquear URL directa si el módulo está en «sin acceso».
  useEffect(() => {
    const can = (item) => {
      const mods = item?.modules || (item?.module ? [item.module] : []);
      if (isCajero && !mods.includes('pos')) return false;
      if (!mods.length) return !isCajero;
      // Hub con varias superficies: visible si CUALQUIERA está permitida.
      return mods.some((m) => puedeVer(m));
    };
    if (isCajero && !location.pathname.startsWith('/tenant/administracion')) {
      navigate('/tenant/administracion/pos', { replace: true });
      return;
    }
    if (!permisosCargados) return;
    const mod = moduleForPath(location.pathname);
    if (mod && !can({ module: mod })) {
      const first = [...CORE_NAV_ITEMS, ...ADVANCED_NAV_ITEMS].find(i => can(i));
      navigate(first?.to || '/tenant/administracion/pos', { replace: true });
    }
  }, [isCajero, location.pathname, navigate, permisosCargados, puedeVer]);

  // S196: la matriz de «Mi Negocio → Permisos» manda en el menú.
  // Cajero: piso de seguridad — solo caja (pos), aunque alguien abra de más.
  const allowItem = (item) => {
    const mods = item.modules || (item.module ? [item.module] : []);
    if (isCajero && !mods.includes('pos')) return false;
    if (!mods.length) return !isCajero;
    // Un hub con varias superficies (p.ej. Social & Ads) se muestra si CUALQUIERA
    // de sus capacidades está permitida; cada pestaña se filtra aparte.
    return mods.some((m) => puedeVer(m));
  };
  const visibleCoreItems = CORE_NAV_ITEMS.filter(allowItem);
  const visibleAdvancedItems = ADVANCED_NAV_ITEMS.filter(allowItem);

  const isAdvancedRoute = ADVANCED_NAV_ITEMS.some((i) => location.pathname.startsWith(i.to));
  const advancedVisible = showAdvanced || isAdvancedRoute;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const SidebarContent = ({ isMobile }) => (
    <>
      <div className="h-14 md:h-16 flex items-center px-5 border-b border-[#E2E8F0]">
        {/* El logo del negocio manda; el nombre de CavernicolApp es el respaldo.
            Se usa el logo HORIZONTAL porque este hueco es alargado: el cuadrado
            aquí sale minúsculo o deformado. Si el negocio no subió el ancho, se
            queda el nombre — nunca un cuadro roto. */}
        {marca?.tiene_logo_ancho ? (
          <img
            src={urlDeLogo(marca.tenant_id, 'wide', marca.sello_ancho)}
            alt={marca.nombre_negocio || 'Logo del negocio'}
            className="max-h-9 max-w-[170px] object-contain"
            data-testid="logo-negocio-barra"
          />
        ) : marca?.producto_logo_url ? (
          <img src={marca.producto_logo_url} alt={marca.producto_nombre || 'Marca'}
               className="max-h-9 max-w-[170px] object-contain"
               data-testid="logo-partner-barra" />
        ) : (
          <h1 className="text-lg font-bold tracking-tight"
              style={{ color: marca?.producto_color || '#0F172A' }}>
            {marca?.producto_nombre || (
              <>Cavernicol<span className="text-[#F72585]">App</span></>
            )}
          </h1>
        )}
        {isMobile && (
          <button onClick={() => setMobileOpen(false)} className="ml-auto text-[#64748B] hover:text-[#0F172A] p-1">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="px-5 py-3 border-b border-[#E2E8F0]">
        <div className="flex items-center gap-2">
          {/* Este hueco es cuadrado, así que aquí sí sirve el logo cuadrado.
              Antes había un icono genérico de edificio, con lo cual el logo
              cuadrado que le pedimos al negocio no aparecía en NINGUNA parte del
              sistema: un archivo pedido para nada. */}
          {marca?.tiene_logo_cuadrado ? (
            <img
              src={urlDeLogo(marca.tenant_id, 'square', marca.sello_cuadrado)}
              alt={marca.nombre_negocio || 'Logo del negocio'}
              className="w-5 h-5 rounded object-contain shrink-0"
              data-testid="logo-negocio-cuadrado"
            />
          ) : (
            <Building2 className="w-4 h-4 text-[#F72585]" strokeWidth={1.5} />
          )}
          <span className="text-xs font-semibold text-[#0F172A] truncate">
            {user?.tenant_role?.replace(/_/g, ' ')?.toUpperCase() || 'TENANT'}
          </span>
        </div>
      </div>

      <nav className="flex-1 py-3 space-y-0.5 overflow-y-auto">
        {visibleCoreItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`tenant-nav-${(item.labelKey || item.label || '').toString().toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 mx-2 px-3 py-2.5 rounded-lg transition-all text-sm ${
                isActive
                  ? 'bg-[#F8F9FA] text-[#0F172A] font-medium border-l-[3px] border-[#F72585]'
                  : 'text-[#64748B] hover:text-[#0F172A] hover:bg-[#F8F9FA] border-l-[3px] border-transparent'
              }`
            }
          >
            <item.icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
            <span>{item.labelKey ? t(item.labelKey) : item.label}</span>
          </NavLink>
        ))}

        {visibleAdvancedItems.length > 0 && (
          <div className="mt-2 pt-2 border-t border-[#E2E8F0]">
            <div className="px-4 py-2"><LanguageSelect className="w-full" /></div>
      <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className="flex items-center gap-3 mx-2 px-3 py-2.5 rounded-lg transition-all text-sm text-[#64748B] hover:text-[#0F172A] hover:bg-[#F8F9FA] w-[calc(100%-1rem)]"
              data-testid="tenant-nav-advanced-toggle"
            >
              <Settings2 className="w-[18px] h-[18px]" strokeWidth={1.5} />
              <span className="flex-1 text-left">{t('nav.moreOptions')}</span>
              <span className="text-[11px] text-[#94A3B8]">{advancedVisible ? t('nav.hide') : t('nav.show')}</span>
            </button>
          </div>
        )}

        {advancedVisible && visibleAdvancedItems.length > 0 && (
          <div className="space-y-0.5" data-testid="tenant-nav-advanced-section">
            {visibleAdvancedItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`tenant-nav-${(item.labelKey || item.label || '').toString().toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 mx-2 px-3 py-2.5 rounded-lg transition-all text-sm ${
                    isActive
                      ? 'bg-[#F8F9FA] text-[#0F172A] font-medium border-l-[3px] border-[#F72585]'
                      : 'text-[#64748B] hover:text-[#0F172A] hover:bg-[#F8F9FA] border-l-[3px] border-transparent'
                  }`
                }
              >
                <item.icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
                <span>{item.labelKey ? t(item.labelKey) : item.label}</span>
              </NavLink>
            ))}
          </div>
        )}
      </nav>

      <div className="border-t border-[#E2E8F0] p-3">
        <div className="flex items-center gap-3 px-2 py-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-[#0F172A] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[#0F172A] font-medium truncate">{user?.name}</p>
            <p className="text-[10px] text-[#64748B] truncate">{user?.email}</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={handleLogout}
          className="w-full justify-start text-[#64748B] hover:text-[#0F172A] hover:bg-[#F8F9FA]" data-testid="tenant-logout-button">
          <LogOut className="w-4 h-4 mr-2" />
          <span className="text-sm">{t('nav.logout')}</span>
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-[#F8F9FA]" data-testid="tenant-layout"
      data-marca={marca?.acento || 'rosa'}
      style={colorDelNegocio}>
      {isImpersonating && <ImpersonationBanner />}

      {/* Desktop Sidebar */}
      <aside className="w-64 bg-white border-r border-[#E2E8F0] hidden md:flex flex-col flex-shrink-0" data-testid="tenant-sidebar">
        <SidebarContent isMobile={false} />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-tenant-sidebar">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white flex flex-col z-10 shadow-xl">
            <SidebarContent isMobile={true} />
          </aside>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="h-14 md:h-16 flex items-center justify-between px-4 md:px-6 bg-white/80 backdrop-blur-md border-b border-[#E2E8F0] sticky top-0 z-40" data-testid="tenant-topbar">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button onClick={() => setMobileOpen(true)} className="md:hidden p-1.5 text-[#64748B] hover:text-[#0F172A]" data-testid="mobile-tenant-menu-btn">
              <Menu className="w-5 h-5" />
            </button>
            <div className="relative flex-1 max-w-xs hidden sm:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <input type="text" placeholder={t('nav.search')}
                className="w-full pl-9 pr-3 py-2 text-sm bg-[#F8F9FA] border border-[#E2E8F0] rounded-lg focus:border-[#0F172A] focus:ring-1 focus:ring-[#0F172A] outline-none transition-all placeholder:text-[#94A3B8]" />
            </div>
            <span className="md:hidden text-base font-bold text-[#0F172A] tracking-tight">
              C<span className="text-[#F72585]">A</span>
            </span>
          </div>
          {/* El modo oscuro es preferencia de la PERSONA, no del negocio: el color
              de marca lo eligió el dueño, pero trabajar con fondo claro u oscuro
              es comodidad de quien tiene la pantalla enfrente. Por eso vive aquí
              arriba, a un clic, y no enterrado en la configuración del negocio. */}
          <button onClick={alternarOscuro} data-testid="tenant-modo-oscuro"
            title={oscuro ? 'Cambiar a fondo claro' : 'Cambiar a fondo oscuro'}
            aria-label={oscuro ? 'Cambiar a fondo claro' : 'Cambiar a fondo oscuro'}
            className="p-2 text-[#64748B] hover:text-[#0F172A] transition-colors">
            {oscuro ? <Sun className="w-5 h-5" strokeWidth={1.5} />
                    : <Moon className="w-5 h-5" strokeWidth={1.5} />}
          </button>

          <button className="relative p-2 text-[#64748B] hover:text-[#0F172A] transition-colors">
            <Bell className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          <div className="animate-fade-in-up">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}
